// app/(routes)/ai-workflows/page.tsx
// v2

// app/(routes)/ai-workflows/page.tsx
"use client";

// --- Imports ---
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

import { PersonalizedUseCaseListComponent } from "@/app/features/ai-workflows/components/PersonalizedUseCaseListComponent";
import { usePersonalizedUseCasesHook } from "@/app/features/ai-workflows/services_and_hooks/usePersonalizedUseCasesHook";
import { useRealtimePucNotifications } from "@/app/features/ai-workflows/services_and_hooks/useRealTimePucNotifications";
import { ProtectedViewComponent } from "@/app/shared/components/ProtectedViewComponent";

export default function PersonalizedUseCasesPage() {
  const router = useRouter();
  const pathname = usePathname();

  // 🔥 FIX: Initialize real-time notifications to ensure UI updates
  useRealtimePucNotifications();

  const {
    // Auth (the refresh below only runs for a signed in user)
    user,

    // View mode latch from Zustand (this page owns the "list" side)
    viewMode,
    setViewMode,

    // Data from TanStack Query (specifically the list of use cases)
    userPersonalizedUseCases,
    totalCount,
    isLoadingUserUseCases,
    isErrorUserUseCases,
    errorUserUseCases,
    refetchUserUseCases,

    // Pagination data and controls
    pagination,

    // Actions from Zustand/Combined logic
    resetFlow,
  } = usePersonalizedUseCasesHook();

  const userId = user?.id;

  // Entering the list route resets the latch. Without this the persisted
  // viewMode stays "create" forever and the next visit to /ai-workflows/create
  // resumes the previous flow instead of starting a new one.
  useEffect(() => {
    if (pathname === "/ai-workflows" && viewMode !== "list") {
      setViewMode("list");
    }
  }, [pathname, viewMode, setViewMode]);

  // Auto-refresh when returning to the page to catch any missed real-time
  // updates. The generation webhook invalidates the Next.js cache tag, not the
  // browser cache, so without this a finished workflow reads as "pending" until
  // staleTime expires.
  useEffect(() => {
    if (pathname === "/ai-workflows" && userId) {
      refetchUserUseCases();
    }
  }, [pathname, userId, refetchUserUseCases]);

  const handleCreateNew = () => {
    console.log("🚀 Create New button clicked in list page");
    resetFlow();
    router.push("/ai-workflows/create");
  };

  // List mode is the route itself. Child routes own their own pages.
  if (pathname && pathname !== "/ai-workflows") {
    console.log("⏭️ Not on main path, not rendering list page content");
    return null;
  }

  return (
    <ProtectedViewComponent
      showHeader={false} // 🔧 Disable header since layout provides it
      showFooter={false} // 🔧 Disable footer since layout provides it
    >
      <PersonalizedUseCaseListComponent
        useCases={userPersonalizedUseCases}
        totalCount={totalCount}
        isLoading={isLoadingUserUseCases}
        isError={isErrorUserUseCases}
        error={errorUserUseCases as Error | null}
        onCreateNew={handleCreateNew}
        pagination={pagination}
      />
    </ProtectedViewComponent>
  );
}
